// core/modals/expenses/EditExpense.jsx
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Form } from 'react-bootstrap';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { X, DollarSign, Calendar, FileText, Upload } from 'feather-icons-react/build/IconComponents';
import { expenseActions } from '../../action/expenseActions';
import { bankActions } from '../../action/bankActions';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

const MySwal = withReactContent(Swal);

const EditExpense = ({ expenseId, onSuccess, onClose, isOpen, categories }) => {
    // Form state
    const [formData, setFormData] = useState({
        expense_date: new Date(),
        category_id: '',
        amount: '',
        payment_method: 'cash',
        bank_account_id: '',
        reference_no: '',
        paid_to: '',
        description: '',
        notes: '',
        status: 'paid'
    });

    const [bankAccounts, setBankAccounts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [existingBill, setExistingBill] = useState(null);

    // Payment method options
    const paymentMethodOptions = [
        { value: 'cash', label: 'Cash' },
        { value: 'bank_transfer', label: 'Bank Transfer' },
        { value: 'cheque', label: 'Cheque' },
        { value: 'credit_card', label: 'Credit Card' },
        { value: 'upi', label: 'UPI' }
    ];

    // Status options
    const statusOptions = [
        { value: 'paid', label: 'Paid' },
        { value: 'unpaid', label: 'Unpaid' },
        { value: 'pending', label: 'Pending' }
    ];

    // Load expense data
    useEffect(() => {
        if (isOpen && expenseId) {
            loadExpense();
            loadBankAccounts();
        }
    }, [isOpen, expenseId]);

    const loadExpense = async () => {
        try {
            const result = await expenseActions.getExpense(expenseId);
            if (result.success) {
                const expense = result.data;
                setFormData({
                    expense_date: new Date(expense.expense_date),
                    category_id: expense.category_id || '',
                    amount: expense.amount,
                    payment_method: expense.payment_method,
                    bank_account_id: expense.bank_account_id || '',
                    reference_no: expense.reference_no || '',
                    paid_to: expense.paid_to || '',
                    description: expense.description || '',
                    notes: expense.notes || '',
                    status: expense.status
                });
                setExistingBill(expense.bill_attachment);
            }
        } catch (error) {
            console.error('Error loading expense:', error);
        }
    };

    const loadBankAccounts = async () => {
        try {
            const result = await bankActions.getAccounts({ limit: 100 });
            if (result.success) {
                const accounts = result.data.map(acc => ({
                    value: acc.id,
                    label: `${acc.bank_name} - ${acc.account_name} (${acc.account_number?.slice(-4) || 'XXXX'})`,
                    balance: acc.current_balance
                }));
                setBankAccounts(accounts);
            }
        } catch (error) {
            console.error('Error loading bank accounts:', error);
        }
    };

    // Handle input change
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // Handle select change
    const handleSelectChange = (name, option) => {
        setFormData(prev => ({ ...prev, [name]: option?.value || '' }));
    };

    // Handle date change
    const handleDateChange = (date) => {
        setFormData(prev => ({ ...prev, expense_date: date }));
    };

    // Handle file change
    const handleFileChange = (e) => {
        setSelectedFile(e.target.files[0]);
    };

    // Handle remove existing bill
    const handleRemoveBill = () => {
        setExistingBill(null);
    };

    // In EditExpense.jsx - Replace the handleSubmit function
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate amount
        if (!formData.amount || parseFloat(formData.amount) <= 0) {
            MySwal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Please enter a valid amount',
                timer: 2000,
                showConfirmButton: false
            });
            return;
        }

        // Validate expense date
        if (!formData.expense_date) {
            MySwal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Please select an expense date',
                timer: 2000,
                showConfirmButton: false
            });
            return;
        }

        // Validate payment method
        if (!formData.payment_method) {
            MySwal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Please select a payment method',
                timer: 2000,
                showConfirmButton: false
            });
            return;
        }

        setLoading(true);

        // Log form data for debugging
        console.log('Edit Form Data:', {
            id: expenseId,
            expense_date: formData.expense_date,
            category_id: formData.category_id,
            amount: formData.amount,
            payment_method: formData.payment_method,
            bank_account_id: formData.bank_account_id,
            reference_no: formData.reference_no,
            paid_to: formData.paid_to,
            description: formData.description,
            notes: formData.notes,
            status: formData.status
        });

        // Create FormData for file upload
        const submitData = new FormData();

        // Format date properly
        const expenseDate = formData.expense_date instanceof Date
            ? formData.expense_date.toISOString().split('T')[0]
            : formData.expense_date;

        submitData.append('id', expenseId);
        submitData.append('expense_date', expenseDate);
        submitData.append('category_id', formData.category_id || '');
        submitData.append('amount', formData.amount.toString());
        submitData.append('payment_method', formData.payment_method);
        submitData.append('bank_account_id', formData.bank_account_id || '');
        submitData.append('reference_no', formData.reference_no || '');
        submitData.append('paid_to', formData.paid_to || '');
        submitData.append('description', formData.description || '');
        submitData.append('notes', formData.notes || '');
        submitData.append('status', formData.status);

        // Handle file upload
        if (selectedFile) {
            submitData.append('bill_attachment', selectedFile);
        } else if (existingBill === null && formData.bill_attachment) {
            // If existing bill was removed, we need to indicate that
            submitData.append('remove_bill', '1');
        }

        // Log FormData contents for debugging
        console.log('Edit FormData entries:');
        for (let pair of submitData.entries()) {
            console.log(pair[0] + ': ' + pair[1]);
        }

        try {
            const result = await expenseActions.updateExpense(expenseId, submitData);
            console.log('Update expense result:', result);

            if (result && result.success === true) {
                await MySwal.fire({
                    icon: 'success',
                    title: 'Success',
                    text: result.message || 'Expense updated successfully',
                    timer: 1500,
                    showConfirmButton: false
                });
                onSuccess();
            } else {
                await MySwal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: result?.message || 'Failed to update expense',
                    timer: 2000,
                    showConfirmButton: false
                });
            }
        } catch (error) {
            console.error('Error updating expense:', error);
            await MySwal.fire({
                icon: 'error',
                title: 'Error',
                text: error.message || 'Failed to update expense. Please try again.',
                timer: 2000,
                showConfirmButton: false
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal show={isOpen} onHide={onClose} size="lg" centered>
            <Modal.Header className="border-0 pb-0">
                <Modal.Title>
                    <h5 className="mb-0">Edit Expense</h5>
                </Modal.Title>
                <button type="button" className="btn-close" onClick={onClose}>
                    <X size={18} />
                </button>
            </Modal.Header>
            <Modal.Body>
                <Form onSubmit={handleSubmit}>
                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <Form.Label>Expense Date <span className="text-danger">*</span></Form.Label>
                            <div className="input-group">
                                <span className="input-group-text">
                                    <Calendar size={16} />
                                </span>
                                <DatePicker
                                    selected={formData.expense_date}
                                    onChange={handleDateChange}
                                    dateFormat="dd/MM/yyyy"
                                    className="form-control"
                                    maxDate={new Date()}
                                    required
                                />
                            </div>
                        </div>

                        <div className="col-md-6 mb-3">
                            <Form.Label>Category</Form.Label>
                            <Select
                                options={categories?.map(cat => ({
                                    value: cat.id,
                                    label: cat.category_name
                                })) || []}
                                value={categories?.find(c => c.id == formData.category_id)
                                    ? { value: formData.category_id, label: categories.find(c => c.id == formData.category_id)?.category_name }
                                    : null}
                                onChange={(opt) => handleSelectChange('category_id', opt)}
                                placeholder="Select category"
                                isClearable
                            />
                        </div>

                        <div className="col-md-6 mb-3">
                            <Form.Label>Amount <span className="text-danger">*</span></Form.Label>
                            <div className="input-group">
                                <span className="input-group-text">
                                    <DollarSign size={16} />
                                </span>
                                <input
                                    type="number"

                                    className="form-control"
                                    name="amount"
                                    value={formData.amount}
                                    onChange={handleInputChange}
                                    placeholder="Enter amount"
                                    required
                                />
                            </div>
                        </div>

                        <div className="col-md-6 mb-3">
                            <Form.Label>Payment Method <span className="text-danger">*</span></Form.Label>
                            <Select
                                options={paymentMethodOptions}
                                value={paymentMethodOptions.find(m => m.value === formData.payment_method)}
                                onChange={(opt) => handleSelectChange('payment_method', opt)}
                                placeholder="Select method"
                                required
                            />
                        </div>

                        <div className="col-md-6 mb-3">
                            <Form.Label>Bank Account</Form.Label>
                            <Select
                                options={bankAccounts}
                                value={bankAccounts.find(acc => acc.value === formData.bank_account_id)}
                                onChange={(opt) => handleSelectChange('bank_account_id', opt)}
                                placeholder="Select bank account"
                                isClearable
                            />
                        </div>

                        <div className="col-md-6 mb-3">
                            <Form.Label>Reference No</Form.Label>
                            <input
                                type="text"
                                className="form-control"
                                name="reference_no"
                                value={formData.reference_no}
                                onChange={handleInputChange}
                                placeholder="Bill/Invoice/Cheque no"
                            />
                        </div>

                        <div className="col-md-6 mb-3">
                            <Form.Label>Paid To</Form.Label>
                            <input
                                type="text"
                                className="form-control"
                                name="paid_to"
                                value={formData.paid_to}
                                onChange={handleInputChange}
                                placeholder="Vendor/Supplier name"
                            />
                        </div>

                        <div className="col-md-6 mb-3">
                            <Form.Label>Status</Form.Label>
                            <Select
                                options={statusOptions}
                                value={statusOptions.find(s => s.value === formData.status)}
                                onChange={(opt) => handleSelectChange('status', opt)}
                                placeholder="Select status"
                            />
                        </div>

                        <div className="col-12 mb-3">
                            <Form.Label>Description</Form.Label>
                            <textarea
                                className="form-control"
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                rows="2"
                                placeholder="Expense description"
                            ></textarea>
                        </div>

                        <div className="col-12 mb-3">
                            <Form.Label>Notes</Form.Label>
                            <textarea
                                className="form-control"
                                name="notes"
                                value={formData.notes}
                                onChange={handleInputChange}
                                rows="2"
                                placeholder="Additional notes"
                            ></textarea>
                        </div>

                        <div className="col-12 mb-3">
                            <Form.Label>Bill Attachment</Form.Label>
                            {existingBill && (
                                <div className="d-flex align-items-center mb-2 p-2 bg-light rounded">
                                    <FileText size={16} className="me-2" />
                                    <span className="flex-grow-1">{existingBill.split('/').pop()}</span>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-link text-danger p-0"
                                        onClick={handleRemoveBill}
                                    >
                                        <X size={16} />
                                    </button>
                                </div>
                            )}
                            <div className="input-group">
                                <span className="input-group-text">
                                    <Upload size={16} />
                                </span>
                                <input
                                    type="file"
                                    className="form-control"
                                    onChange={handleFileChange}
                                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                />
                            </div>
                            <small className="text-muted">
                                Allowed: PDF, JPG, PNG, DOC (Max 5MB)
                            </small>
                        </div>
                    </div>

                    <div className="d-flex justify-content-end gap-2 mt-3">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" className="btn btn-primary" disabled={loading}>
                            {loading ? 'Updating...' : 'Update Expense'}
                        </button>
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    );
};

// PropTypes validation
EditExpense.propTypes = {
    expenseId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    onSuccess: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    isOpen: PropTypes.bool.isRequired,
    categories: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            category_name: PropTypes.string.isRequired,
            color: PropTypes.string,
            icon: PropTypes.string
        })
    ).isRequired
};

export default EditExpense;